import { useCallback, useEffect, useMemo } from 'react';

import {
  LoadingOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Badge, Empty, Input, Space, Spin, Typography } from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useListFetcher } from '../../../hooks/useListFetcher';
import { useSyncedSearch } from '../../../hooks/useSyncedSearch';
import { useUrlHydration } from '../../../hooks/useUrlHydration';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchUsers,
  hydrateFromUrl,
  searchUsersByNameWithParams,
  setUserSearchQuery,
} from '../../../store/features/users/usersSlice';
import { ListApiConfig } from '../../../types/list';
import { UserPermissions } from '../../../util';
import { buildListParams } from '../../../util/listApiParams';
import { DrawerHashRoute } from '../../containers/Drawers/types';
import Button from '../../elements/Button';
import NoPermission from '../../elements/NoPermission';
import NoResultsFound from '../../elements/NoResultsFound';
import ViewWrapper from '../../elements/ViewWrapper';
import UserTableList from './components/UserTableList';
import './UsersView.less';

const { Title } = Typography;

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

const UsersView = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const { hasPermission } = useAuth();
  const listApiConfig: ListApiConfig = useMemo(
    () => ({
      defaultSort: 'updated_at:desc',
      columns: 'roles.display_name,roles.name,roles.id|asArray',
      searchParamKey: 'name',
      searchTransformer: (s) => `*${s.toLowerCase()}*`,
    }),
    []
  );

  const {
    value: users,
    fetchingUsers,
    searchingUsers,
    totalCount,
  } = useAppSelector((state) => state.users);
  const {
    page,
    pageSize,
    searchQuery: storeSearch,
    sort,
  } = useAppSelector((state) => state.users);

  const hasNoSearchResultsFound = useMemo(
    () => !fetchingUsers && !!storeSearch && users.length === 0,
    [fetchingUsers, storeSearch, users.length]
  );

  const hasUsers = !(!fetchingUsers && users.length === 0);

  // removed: params computed via Redux -> buildParams

  const createNewUserButton = useMemo(
    (): JSX.Element => (
      <Link to={DrawerHashRoute.UserForm}>
        <Button type="primary" data-cy="create-new-user-btn">
          Create New User
          <UserAddOutlined />
        </Button>
      </Link>
    ),
    []
  );

  const { hasHydrated } = useUrlHydration({
    location,
    controlsFromStore: {
      page,
      pageSize,
      search: storeSearch,
      sort,
    },
    onHydrate: (urlControls) => {
      // Sanitize unsupported sort fields from URL (e.g., last_login_at is not server-sortable)
      const next = { ...urlControls } as any;
      if (next.sort) {
        const field = String(next.sort).split(':')[0];
        const allowed = new Set(['updated_at']);
        if (!allowed.has(field)) {
          delete next.sort;
        }
      }
      dispatch(hydrateFromUrl(next));
    },
  });

  // Guard against invalid persisted sort (e.g., last_login_at) when URL has no sort
  useEffect(() => {
    if (!hasHydrated) return;
    const allowed = new Set(['updated_at']);
    const currentField = String(sort || '').split(':')[0];
    if (currentField && !allowed.has(currentField)) {
      dispatch(hydrateFromUrl({ sort: 'updated_at:desc', page: 1 }));
    }
  }, [hasHydrated, sort, dispatch]);

  const fetchUsersThunk = useCallback(
    (params: Record<string, any>) => dispatch(fetchUsers({ params })),
    [dispatch]
  );

  useListFetcher({
    hasHydrated,
    controlsFromStore: {
      page,
      pageSize,
      search: storeSearch,
      sort,
    },
    config: listApiConfig,
    fetchThunk: fetchUsersThunk,
  });

  const onSearch = useCallback(
    (value: string) => {
      const params = buildListParams(
        { page: 1, pageSize, search: value, sort },
        listApiConfig
      );
      return dispatch(searchUsersByNameWithParams({ params }));
    },
    [dispatch, pageSize, sort, listApiConfig]
  );

  const { searchValue, onChange, clear } = useSyncedSearch({
    storeSearchValue: storeSearch,
    setStoreSearch: (value) => dispatch(setUserSearchQuery(value)),
    pageSize,
    sort,
    history,
    location,
    onSearch,
  });

  const headerSectionRight = useMemo(
    (): JSX.Element => (
      <Space direction="horizontal" size="middle">
        <Input
          allowClear={!searchingUsers}
          value={searchValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search for a user..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
          suffix={searchingUsers && <Spin indicator={antSpinIcon} />}
        />
        {hasPermission(UserPermissions.UsersCreate) && createNewUserButton}
      </Space>
    ),
    [createNewUserButton, searchValue, searchingUsers, hasPermission, onChange]
  );

  // Effects replaced by shared hooks: useUrlHydration, useListFetcher, useSyncedSearch

  if (!hasPermission(UserPermissions.UsersView)) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={
        <Space direction="horizontal">
          <Title level={3}>Users</Title>
          <Badge
            size="default"
            count={totalCount as number}
            className="total-count-badge"
          />
        </Space>
      }
      headerSectionRight={headerSectionRight}
    >
      {hasUsers ? (
        <UserTableList loading={fetchingUsers} users={users} />
      ) : hasNoSearchResultsFound ? (
        <NoResultsFound searchQuery={searchValue} onClear={clear} />
      ) : (
        <Empty
          description="Currently no users."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {hasPermission(UserPermissions.UsersCreate) && createNewUserButton}
        </Empty>
      )}
    </ViewWrapper>
  );
};

export default UsersView;
